import hoistStatics from 'hoist-non-react-statics';
import { Component, FC, useCallback, useEffect, useRef, useState } from 'react';
import { usePrevious } from 'react-use';

interface Props {
  isFetching: boolean;
}

interface State {
  showRefreshing: boolean;
  refreshingFrom: number;
}

const MIN_REFRESHING_DISPLAY_TIME = 1000;

export interface RefreshControlProps {
  onRefreshHOC: () => void;
  showRefreshing: boolean;
}

/**
 * Hook 版本的 withRefreshControl，基本就是複製貼上，除了 componentDidUpdate 外沒有改到什麼邏輯
 *
 * NOTE: 用 effect 與 ref 實作 refreshing end 的判斷
 */
export const useRefreshControl = ({ isFetching }: { isFetching: boolean }) => {
  const [showRefreshing, setShowRefreshing] = useState(false);
  const refreshingFrom = useRef(0);
  const reloadUIDelay = useRef<NodeJS.Timer | null>(null);

  const onRefreshHOC = useCallback(() => {
    refreshingFrom.current = Date.now();

    setShowRefreshing(true);
  }, [setShowRefreshing]);

  const prevIsFetching = usePrevious(isFetching);

  useEffect(() => {
    const refreshEnd = prevIsFetching === true && isFetching === false;

    if (!showRefreshing) {
      return;
    }

    const clear = () => {
      if (reloadUIDelay.current) {
        clearTimeout(reloadUIDelay.current);
        reloadUIDelay.current = null;
      }
    };

    // refreshing end
    if (refreshEnd) {
      // api 回應時間
      const durning = Date.now() - refreshingFrom.current;

      // 確保 setTimeout 一定大於 0
      const reloadExtension = Math.max(
        MIN_REFRESHING_DISPLAY_TIME - durning,
        0
      );

      // to the best UX practice, reloading UI have to showing 1000ms at least
      reloadUIDelay.current = setTimeout(() => {
        setShowRefreshing(false);
        clear();
      }, Math.min(reloadExtension, MIN_REFRESHING_DISPLAY_TIME));
    }

    return () => {
      clear();
    };
  }, [showRefreshing, isFetching]);

  return { onRefreshHOC, showRefreshing };
};

export default (WrappedComponent: typeof Component | FC<any>) => {
  class RefreshControl extends Component<Props, State> {
    state = {
      showRefreshing: false,
      refreshingFrom: 0,
    };

    reloadUIDelay?: any | null;

    componentDidUpdate(prevProps) {
      const { showRefreshing } = this.state;

      if (!showRefreshing) {
        return;
      }

      // refreshing end
      if (prevProps.isFetching === true && this.props.isFetching === false) {
        // api 回應時間
        const durning = Date.now() - this.state.refreshingFrom;

        // 確保 setTimeout 一定大於 0
        const reloadExtension = Math.max(
          MIN_REFRESHING_DISPLAY_TIME - durning,
          0
        );

        // to the best UX practice, reloading UI have to showing 1000ms at least
        this.reloadUIDelay = setTimeout(() => {
          this.setState({ showRefreshing: false });
          this.clearReloadUIDelayTimeout();
        }, Math.min(reloadExtension, MIN_REFRESHING_DISPLAY_TIME));
      }
    }

    componentWillUnmount = () => {
      this.clearReloadUIDelayTimeout();
    };

    clearReloadUIDelayTimeout = () => {
      if (this.reloadUIDelay) {
        clearTimeout(this.reloadUIDelay);
        this.reloadUIDelay = null;
      }
    };

    onRefreshHOC = () => {
      this.setState({ showRefreshing: true, refreshingFrom: Date.now() });
    };

    render() {
      const { showRefreshing } = this.state;

      return (
        <WrappedComponent
          {...this.props}
          onRefreshHOC={this.onRefreshHOC}
          showRefreshing={showRefreshing}
        />
      );
    }
  }

  return hoistStatics(RefreshControl, WrappedComponent);
};
